import { useState } from "react";
import WilayahPicker from "./WilayahPicker";
import { PhoneInput } from "./PhoneInput";
import { teleponValid, PESAN_TELEPON } from "../lib/phone";

// PR keamanan lanjutan B (butir 10): batas = BATAS_ISIAN_KA server (server/ka-isian.js; jest menjaga angkanya sama) — server tetap penentu (422)
export const BATAS_ALAMAT = { nama_penerima: 40, alamat: 200, alamat_min: 10, patokan: 50 };
const rata = (v) => String(v || "").replace(/\s+/g, " ").trim();
/** Panjang "jalan, kelurahan, kecamatan, kota" (= destination_address yang dikirim ke KiriminAja) dan sisa karakter untuk isian jalan */
export function sisaAlamat(v) {
  const ekor = [v?.kelurahan, v?.kecamatan, v?.kota].map(rata).filter(Boolean).join(", ");
  const ruang = BATAS_ALAMAT.alamat - ekor.length - (ekor ? 2 : 0);
  return { ruang: Math.max(0, ruang), sisa: ruang - rata(v?.jalan).length };
}
const Sisa = ({ sisa, tid }) => (
  <p className="mt-1 text-xs" style={{ color: sisa < 0 ? MERAH : "#6E6E73" }} data-testid={tid}>
    {sisa < 0 ? `Kelebihan ${-sisa} karakter` : `Sisa ${sisa} karakter`}
  </p>
);

const inputCls = "mt-1 w-full rounded-[4px] border border-[#E5E5EA] bg-white px-3 py-2 text-sm text-[#1D1D1F] outline-none focus:border-[#1D1D1F]";
const MERAH = "#A12622";

export const alamatKosong = () => ({
  nama_alamat: "", nama_penerima: "", telepon: "", jalan: "", kelurahan: "", kecamatan: "",
  kota: "", provinsi: "", kode_pos: "", patokan: "",
  ka_kecamatan_id: 0, ka_kelurahan_id: 0, ka_kota_id: 0, ka_label: "", ka_kota: "", ka_provinsi: "",
  utama: false,
});

export const wilayahTerverifikasi = (a) => Number(a?.ka_kecamatan_id) > 0 && Number(a?.ka_kelurahan_id) > 0;

export const validasiAlamat = (v, { withNamaAlamat = true } = {}) => {
  const e = {};
  if (withNamaAlamat && !String(v.nama_alamat || "").trim()) e.nama_alamat = "Nama alamat wajib diisi.";
  if (!String(v.nama_penerima || "").trim()) e.nama_penerima = "Nama penerima wajib diisi.";
  if (!teleponValid(v.telepon)) e.telepon = PESAN_TELEPON;
  if (!String(v.jalan || "").trim()) e.jalan = "Alamat lengkap wajib diisi.";
  else if (sisaAlamat(v).sisa < 0) e.jalan = `Alamat terlalu panjang: jalan, kelurahan, kecamatan, dan kota bersama-sama maksimal ${BATAS_ALAMAT.alamat} karakter.`;
  if (rata(v.nama_penerima).length > BATAS_ALAMAT.nama_penerima) e.nama_penerima = `Nama penerima maksimal ${BATAS_ALAMAT.nama_penerima} karakter.`;
  if (rata(v.patokan).length > BATAS_ALAMAT.patokan) e.patokan = `Patokan maksimal ${BATAS_ALAMAT.patokan} karakter.`;
  if (!wilayahTerverifikasi(v)) e.wilayah = "Pilih kelurahan & kecamatan dari daftar.";
  return e;
};

const Galat = ({ teks, tid }) => teks ? (
  <p className="mt-1 text-xs font-medium" style={{ color: MERAH }} data-testid={tid}>{teks}</p>
) : null;

export const alamatSatuBaris = (a) => [
  a?.jalan,
  a?.kelurahan && `Kel. ${a.kelurahan}`,
  a?.kecamatan && `Kec. ${a.kecamatan}`,
  a?.ka_kota || a?.kota,
  [a?.ka_provinsi || a?.provinsi, a?.kode_pos].filter(Boolean).join(" "),
].filter(Boolean).join(", ");

/**
 * Form alamat tunggal yang dipakai di buku alamat MAUPUN checkout.
 * Pemilih wilayah persis sama dengan checkout (WilayahPicker).
 */
export default function AddressForm({ value, onChange, errors = {}, onSentuh, withNamaAlamat = true, withUtama = true, tid = "alamat" }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const [telBlur, setTelBlur] = useState(false);
  const telErr = teleponValid(value.telepon) ? "" : (errors.telepon || (telBlur ? PESAN_TELEPON : ""));

  return (
    <div data-testid={`${tid}-form`}>
      {withNamaAlamat && (
        <div>
          <label className="text-sm font-medium">Nama alamat <span style={{ color: MERAH }}>*</span></label>
          <input value={value.nama_alamat || ""} onChange={set("nama_alamat")} className={inputCls}
            placeholder="cth: Gudang, Toko Tebet" data-testid={`${tid}-nama-alamat`} />
          <Galat teks={errors.nama_alamat} tid={`${tid}-err-nama-alamat`} />
        </div>
      )}

      <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${withNamaAlamat ? "mt-3" : ""}`}>
        <div>
          <label className="text-sm font-medium">Nama penerima <span style={{ color: MERAH }}>*</span></label>
          <input value={value.nama_penerima || ""} onChange={set("nama_penerima")} className={inputCls} data-testid={`${tid}-penerima`}
            maxLength={BATAS_ALAMAT.nama_penerima} />
          <Galat teks={errors.nama_penerima} tid={`${tid}-err-penerima`} />
        </div>
        <div>
          <label className="text-sm font-medium">Telepon penerima <span style={{ color: MERAH }}>*</span></label>
          <PhoneInput value={value.telepon || ""} onChange={(v) => onChange({ ...value, telepon: v })} className={inputCls}
            onBlur={() => setTelBlur(true)} data-testid={`${tid}-telepon`} />
          <Galat teks={telErr} tid={`${tid}-err-telepon`} />
        </div>
      </div>

      <div className="mt-3">
        <WilayahPicker nilai={value} tid={`${tid}-wilayah`} onSentuh={onSentuh}
          onPilih={(w) => onChange({
            ...value,
            kelurahan: w ? w.kelurahan : "", kecamatan: w ? w.kecamatan : "",
            kota: w ? w.kota : "", provinsi: w ? w.provinsi : "",
            kode_pos: w && w.kode_pos ? w.kode_pos : (w ? "" : value.kode_pos),
            ka_kecamatan_id: w ? w.kecamatan_id : 0, ka_kelurahan_id: w ? w.kelurahan_id : 0,
            ka_label: w ? w.label : "", ka_kota: w ? w.kota : "", ka_provinsi: w ? w.provinsi : "",
            ka_kota_id: w ? (w.kota_id || 0) : 0,
          })} />
        <Galat teks={errors.wilayah} tid={`${tid}-err-wilayah`} />
      </div>

      <div className="mt-3">
        <label className="text-sm font-medium">Alamat lengkap <span style={{ color: MERAH }}>*</span></label>
        <input value={value.jalan || ""} onChange={set("jalan")} className={inputCls}
          placeholder="Nama jalan, nomor, RT/RW, blok" data-testid={`${tid}-jalan`} maxLength={Math.max(1, sisaAlamat(value).ruang)} />
        {errors.jalan ? <Galat teks={errors.jalan} tid={`${tid}-err-jalan`} /> : <Sisa sisa={sisaAlamat(value).sisa} tid={`${tid}-sisa-jalan`} />}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="text-sm font-medium">Kode pos</label>
          <input value={value.kode_pos || ""} onChange={set("kode_pos")} className={inputCls}
            placeholder="otomatis dari wilayah" data-testid={`${tid}-kode-pos`} />
        </div>
        <div>
          <label className="text-sm font-medium">Patokan untuk kurir <span className="text-[#6E6E73]">(opsional)</span></label>
          <input value={value.patokan || ""} onChange={set("patokan")} className={inputCls}
            placeholder="cth: pagar hitam seberang masjid" data-testid={`${tid}-patokan`} maxLength={BATAS_ALAMAT.patokan} />
          {errors.patokan ? <Galat teks={errors.patokan} tid={`${tid}-err-patokan`} />
            : <Sisa sisa={BATAS_ALAMAT.patokan - rata(value.patokan).length} tid={`${tid}-sisa-patokan`} />}
        </div>
      </div>

      {withUtama && (
        <label className="mt-4 flex items-center gap-2 text-sm text-[#1D1D1F]">
          <input type="checkbox" checked={!!value.utama} onChange={(e) => onChange({ ...value, utama: e.target.checked })}
            className="h-4 w-4 accent-[#1D1D1F]" data-testid={`${tid}-utama`} />
          Jadikan alamat utama
        </label>
      )}
    </div>
  );
}
